import React from "react";
import {
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
  Info,
  X,
  ArrowRight
} from "lucide-react";

export type AlertVariant = "error" | "warning" | "info" | "success";

export interface AlertBoxAction {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
}

export interface AlertBoxProps {
  variant?: AlertVariant;
  title?: string;
  message: React.ReactNode;
  action?: AlertBoxAction;
  secondaryAction?: AlertBoxAction;
  onDismiss?: () => void;
  className?: string;
}

const VARIANT_CONFIG = {
  error: {
    container:
      "border-rose-500/35 bg-[#161114]/95 text-rose-200 shadow-[0_8px_30px_rgba(244,63,94,0.15)] ring-1 ring-rose-500/20",
    badge: "bg-rose-500/15 border-rose-500/30 text-rose-400",
    title: "text-rose-100",
    description: "text-rose-200/90",
    primaryButton:
      "bg-rose-500/20 hover:bg-rose-500/30 text-rose-200 border-rose-500/40 hover:border-rose-400 focus:ring-rose-500/30",
    secondaryButton: "text-rose-300 hover:text-white",
    closeButton: "text-rose-400 hover:text-rose-100 hover:bg-rose-500/20",
    Icon: AlertCircle,
  },
  warning: {
    container:
      "border-amber-500/35 bg-[#18140c]/95 text-amber-200 shadow-[0_8px_30px_rgba(245,158,11,0.15)] ring-1 ring-amber-500/20",
    badge: "bg-amber-500/15 border-amber-500/30 text-amber-400",
    title: "text-amber-100",
    description: "text-amber-200/90",
    primaryButton:
      "bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border-amber-500/40 hover:border-amber-400 focus:ring-amber-500/30",
    secondaryButton: "text-amber-300 hover:text-white",
    closeButton: "text-amber-400 hover:text-amber-100 hover:bg-amber-500/20",
    Icon: AlertTriangle,
  },
  info: {
    container:
      "border-cyan-500/35 bg-[#0c141a]/95 text-cyan-200 shadow-[0_8px_30px_rgba(6,182,212,0.15)] ring-1 ring-cyan-500/20",
    badge: "bg-cyan-500/15 border-cyan-500/30 text-cyan-400",
    title: "text-cyan-100",
    description: "text-cyan-200/90",
    primaryButton:
      "bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-200 border-cyan-500/40 hover:border-cyan-400 focus:ring-cyan-500/30",
    secondaryButton: "text-cyan-300 hover:text-white",
    closeButton: "text-cyan-400 hover:text-cyan-100 hover:bg-cyan-500/20",
    Icon: Info,
  },
  success: {
    container:
      "border-emerald-500/35 bg-[#0e1713]/95 text-emerald-200 shadow-[0_8px_30px_rgba(16,185,129,0.15)] ring-1 ring-emerald-500/20",
    badge: "bg-emerald-500/15 border-emerald-500/30 text-emerald-400",
    title: "text-emerald-100",
    description: "text-emerald-200/90",
    primaryButton:
      "bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border-emerald-500/40 hover:border-emerald-400 focus:ring-emerald-500/30",
    secondaryButton: "text-emerald-300 hover:text-white",
    closeButton: "text-emerald-400 hover:text-emerald-100 hover:bg-emerald-500/20",
    Icon: CheckCircle2,
  },
};

export const AlertBox: React.FC<AlertBoxProps> = ({
  variant = "error",
  title,
  message,
  action,
  secondaryAction,
  onDismiss,
  className = "",
}) => {
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.error;
  const Icon = config.Icon;

  return (
    <div
      role="alert"
      aria-live="polite"
      className={`relative overflow-hidden rounded-2xl border p-4 backdrop-blur-md transition-all duration-200 animate-in fade-in slide-in-from-top-1.5 before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/[0.06] before:to-transparent before:pointer-events-none ${config.container} ${className}`}
    >
      <div className="flex items-start gap-3.5">
        {/* Recessed Icon Badge */}
        <div
          className={`h-9 w-9 rounded-xl border flex items-center justify-center shrink-0 shadow-sm ${config.badge}`}
        >
          <Icon className="h-4.5 w-4.5" />
        </div>

        {/* Content Area */}
        <div className="flex-1 min-w-0 pr-1">
          {title && (
            <h4
              className={`text-xs font-bold tracking-tight mb-1 flex items-center gap-1.5 ${config.title}`}
            >
              <span>{title}</span>
            </h4>
          )}

          <div
            className={`text-xs leading-relaxed font-normal break-words ${config.description}`}
          >
            {message}
          </div>

          {/* Action Row */}
          {(action || secondaryAction) && (
            <div className="mt-3 flex flex-wrap items-center gap-2.5 pt-1">
              {action && (
                <button
                  type="button"
                  onClick={action.onClick}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all duration-150 focus:outline-none focus:ring-2 shadow-sm ${config.primaryButton}`}
                >
                  <span>{action.label}</span>
                  {action.icon ? (
                    action.icon
                  ) : (
                    <ArrowRight className="h-3.5 w-3.5" />
                  )}
                </button>
              )}

              {secondaryAction && (
                <button
                  type="button"
                  onClick={secondaryAction.onClick}
                  className={`text-xs underline font-medium transition-colors ${config.secondaryButton}`}
                >
                  {secondaryAction.label}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Close Button */}
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss alert"
            className={`-mr-1 -mt-1 p-1 rounded-lg transition-colors focus:outline-none shrink-0 ${config.closeButton}`}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default AlertBox;
